import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/common";
import { EvidencePanel } from "../components/EvidencePanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function CycleCounts() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ warehouse: "", location: "" });
  const [detail, setDetail] = useState(null);
  const [counts, setCounts] = useState({});

  const load = useCallback(() => api.get("/cycle-counts").then((r) => setRows(r.data)), []);
  useEffect(() => {
    load();
    api.get("/warehouses").then((r) => setWarehouses(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
  }, [load]);

  const create = async () => {
    try {
      const { data } = await api.post("/cycle-counts", form);
      toast.success(t("common.created"));
      setOpen(false);
      setForm({ warehouse: "", location: "" });
      setDetail(data);
      setCounts({});
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = (cc) => {
    setDetail(cc);
    const init = {};
    cc.lines.forEach((l) => {
      if (l.counted_qty != null) init[`${l.item_number}|${l.location}`] = l.counted_qty;
    });
    setCounts(init);
  };

  const saveCounts = async () => {
    try {
      const lines = Object.entries(counts).filter(([, v]) => v !== "").map(([k, v]) => {
        const [item_number, location] = k.split("|");
        return { item_number, location, counted_qty: Number(v) };
      });
      const { data } = await api.put(`/cycle-counts/${detail.id}/count`, { lines });
      toast.success(t("cc.saved"));
      setDetail(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const postCount = async () => {
    try {
      const { data } = await api.post(`/cycle-counts/${detail.id}/post`);
      toast.success(t("cc.posted"));
      setDetail(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="cycle-counts-page">
      <PageHeader title={t("cc.title")} subtitle={t("cc.subtitle")}>
        {can("admin", "warehouse") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="count-new-button">
                <Plus size={13} className="mr-1" /> {t("cc.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{t("cc.new")}</DialogTitle>
                <DialogDescription className="text-xs">{t("cc.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.warehouse")}</Label>
                  <Select value={form.warehouse} onValueChange={(v) => setForm({ ...form, warehouse: v, location: "" })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="count-form-warehouse"><SelectValue /></SelectTrigger>
                    <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("loc.bin")}</Label>
                  <Select value={form.location || "none"} onValueChange={(v) => setForm({ ...form, location: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="count-form-location"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("common.all")}</SelectItem>
                      {locations.filter((l) => l.warehouse === form.warehouse).map((l) => <SelectItem key={l.id} value={l.code}>{l.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={create} disabled={!form.warehouse} className="rounded-none font-bold" data-testid="count-create-button">{t("common.create")}</Button>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="counts-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("common.number")}</th><th>{t("common.status")}</th><th>{t("cc.scope")}</th>
              <th className="num">{t("common.lines")}</th><th>{t("common.user")}</th><th>{t("common.date")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="cursor-pointer" onClick={() => openDetail(r)}>
                  <td className="font-bold">{r.number}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>{r.warehouse}{r.location ? ` / ${r.location}` : ""}</td>
                  <td className="num">{r.lines.length}</td>
                  <td className="text-muted-foreground">{r.created_by}</td>
                  <td className="text-muted-foreground">{fmtDate(r.created_at)}</td>
                  <td><span className="text-[10px] font-bold text-primary" data-testid={`count-view-${r.number}`}>{t("common.view")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none sm:max-w-3xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="uppercase font-black flex items-center gap-3">
                  {detail.number} <StatusBadge status={detail.status} />
                  <span className="text-xs font-normal text-muted-foreground">{detail.warehouse}{detail.location ? ` / ${detail.location}` : ""}</span>
                </DialogTitle>
                <DialogDescription className="text-xs">{t("cc.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="tbl-wrap max-h-80 overflow-y-auto">
                <table className="tbl">
                  <thead><tr>
                    <th>{t("common.item")}</th><th>{t("loc.bin")}</th>
                    <th className="num">{t("cc.systemQty")}</th><th className="num">{t("cc.countedQty")}</th><th className="num">{t("cc.variance")}</th>
                  </tr></thead>
                  <tbody>
                    {detail.lines.map((l) => {
                      const key = `${l.item_number}|${l.location || ""}`;
                      const counted = counts[key] ?? "";
                      const variance = counted !== "" ? Number(counted) - l.system_qty : null;
                      return (
                        <tr key={key}>
                          <td className="font-bold">{l.item_number} <span className="font-normal text-muted-foreground">{l.description}</span></td>
                          <td>{l.location || "—"}</td>
                          <td className="num">{fmtQty(l.system_qty)}</td>
                          <td className="num">
                            {detail.status !== "posted" && can("admin", "warehouse") ? (
                              <Input type="number" value={counted}
                                onChange={(e) => setCounts({ ...counts, [key]: e.target.value })}
                                className="rounded-none h-7 text-xs w-24 inline-block text-right"
                                data-testid={`count-input-${l.item_number}`} />
                            ) : fmtQty(l.counted_qty ?? 0)}
                          </td>
                          <td className={`num font-bold ${variance ? "text-destructive" : ""}`}>
                            {variance != null ? (variance > 0 ? "+" : "") + fmtQty(variance) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2">
                {detail.status !== "posted" && can("admin", "warehouse") && (
                  <Button size="sm" onClick={saveCounts} className="rounded-none text-xs font-bold" data-testid="count-save-button">{t("cc.saveCounts")}</Button>
                )}
                {detail.status === "counted" && can("admin", "approver") && (
                  <Button size="sm" variant="outline" onClick={postCount}
                    className="rounded-none text-xs font-bold border-primary text-primary" data-testid="count-post-button">{t("cc.postCount")}</Button>
                )}
              </div>

              {/* Evidences section */}
              <EvidencePanel
                resourceType="cc"
                basePath={`/cycle-counts/${detail.id}/evidence`}
                canWrite={can("admin", "warehouse", "approver")}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
